/**
 * Pure decision logic for the shared visit-freshness self-heal (see
 * `useVisitSelfHeal`).
 *
 * Background: on mobile the global QueryClient pins `staleTime: Infinity` with no
 * `refetchInterval` and no `refetchOnWindowFocus` override (see
 * `ApplicationQeueryClientProvider`). react-query's focus/reconnect refetch only
 * re-runs queries it considers STALE, so with infinite stale time those triggers
 * are permanent no-ops. Views that add their own overrides (EmployeeCalendar:
 * `refetchInterval: 5000` + `refetchOnWindowFocus: 'always'`) stay live; views
 * that do not (the route browser's per-route `ServiceVisit` lists) never refresh
 * after a freeze/resume — the data drifts stale indefinitely while react-query is
 * online, focused and unpaused (field report v1.11.16: visit-data-stale,
 * rqOnline:true, paused:0, fetching:0, visitDataAgeMs:113104).
 *
 * This module decides, from a sampled snapshot, whether the self-heal should
 * force a refetch of the active visit-list queries. It is kept pure (no
 * react-query / DOM access) so the rules are explicit and unit-testable. The hook
 * forces the refetch via `refetchQueries`, which ignores `staleTime` and so
 * actually runs even under `staleTime: Infinity`.
 */

/**
 * Whether the freshest active visit-list data is stale.
 *
 * `newestDataUpdatedAt` is the max `dataUpdatedAt` across the active
 * `ServiceVisit` by-source queries (0 when none have loaded). A value of 0 means
 * nothing has loaded yet — not staleness — so it never counts as stale.
 */
export const isVisitDataStale = (
  newestDataUpdatedAt: number,
  now: number,
  staleMs: number
): boolean => newestDataUpdatedAt > 0 && now - newestDataUpdatedAt > staleMs

/**
 * Whether the self-heal should force a refetch on this tick.
 *
 * Gated on the page being visible (a hidden tab is expected to have paused
 * polling — refreshing it would be wasteful and is not user-visible), the data
 * actually being stale, and a throttle so a burst of resume events
 * (visibilitychange + resume + focus fire within milliseconds of one wake) and
 * the steady-state interval collapse to at most one refetch per `throttleMs`.
 */
export const shouldRefetchStaleVisits = (
  visible: boolean,
  newestDataUpdatedAt: number,
  now: number,
  lastRefetchAt: number,
  staleMs: number,
  throttleMs: number
): boolean =>
  visible &&
  isVisitDataStale(newestDataUpdatedAt, now, staleMs) &&
  now - lastRefetchAt >= throttleMs
